/* ===== Faceted Filtering (patched) =====
 * - Debounce 800ms (เดิม)
 * - AbortController: ยกเลิกรีเควสต์เก่า กันผลลัพธ์สลับเฟรม
 * - ลด DOMParser ซ้ำซ้อน: parse ครั้งเดียว/รอบ
 * - ป้องกัน null/undefined หลายจุด
 * - Cache จำกัดขนาด (default 10 รายการ)
 * - ใช้ event.target แทน srcElement
 */

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit);
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  /* ---------- Static state ---------- */
  static _ctrl = null;
  static _reqId = 0;
  static _cacheMax = 10;
  static filterData = [];
  static searchParamsInitial = window.location.search.slice(1);
  static searchParamsPrev = window.location.search.slice(1);

  /* ---------- Lifecycle ---------- */
  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  /* ---------- Helpers ---------- */
  static _parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  static _renderAll(html, event, doc) {
    const d = doc || FacetFiltersForm._parse(html);
    FacetFiltersForm.renderFilters(html, event, d);
    FacetFiltersForm.renderProductGridContainer(html, d);
    FacetFiltersForm.renderProductCount(html, d);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
  }

  static _addToCache(entry) {
    FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, entry];
    if (FacetFiltersForm.filterData.length > FacetFiltersForm._cacheMax) {
      FacetFiltersForm.filterData.shift();
    }
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((el) => {
      el.classList.toggle('disabled', disable);
    });
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?' + searchParams}`);
  }

  static getSections() {
    const pg = document.getElementById('product-grid');
    return pg ? [{ section: pg.dataset.id }] : [];
  }

  /* ---------- Main rendering flow ---------- */
  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    if (!sections.length) return;

    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );

    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const gridContainer = document.getElementById('ProductGridContainer');
    const collectionEl = gridContainer?.querySelector('.collection');
    collectionEl?.classList.add('loading');
    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const fromCache = FacetFiltersForm.filterData.find((e) => e.url === url);

      if (fromCache) {
        FacetFiltersForm._renderAll(fromCache.html, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    const reqId = ++FacetFiltersForm._reqId;

    if (FacetFiltersForm._ctrl) FacetFiltersForm._ctrl.abort();
    FacetFiltersForm._ctrl = new AbortController();

    fetch(url, { signal: FacetFiltersForm._ctrl.signal })
      .then((response) => response.text())
      .then((html) => {
        if (reqId !== FacetFiltersForm._reqId) return; // มีรีเควสต์ใหม่กว่าแล้ว
        FacetFiltersForm._addToCache({ html, url });
        const doc = FacetFiltersForm._parse(html);
        FacetFiltersForm._renderAll(html, event, doc);
      })
      .catch((e) => {
        if (e.name !== 'AbortError') console.error('[FacetFilters] fetch error:', e);
      });
  }

  /* ---------- DOM updates (accept doc เพื่อเลี่ยง parse ซ้ำ) ---------- */
  static renderProductGridContainer(html, doc) {
    const d = doc || FacetFiltersForm._parse(html);
    const target = document.getElementById('ProductGridContainer');
    const src = d.getElementById('ProductGridContainer');
    if (!target || !src) return;

    target.innerHTML = src.innerHTML;
    target.querySelectorAll('.scroll-trigger').forEach((el) => {
      el.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount(html, doc) {
    const d = doc || FacetFiltersForm._parse(html);
    const countEl = d.getElementById('ProductCount');
    const count = countEl ? countEl.innerHTML : '';

    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event, doc) {
    const parsedHTML = doc || FacetFiltersForm._parse(html);
    const facetFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // ลบฟิลเตอร์ที่ฝั่งเซิร์ฟเวอร์ไม่ส่งมาแล้ว
    Array.from(facetDom).forEach((currentElement) => {
      if (!Array.from(facetFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const jsFilter = event?.target?.closest?.('.js-filter');
    const matchesId = (element) => (jsFilter ? element.id === jsFilter.id : false);

    const facetsToRender = Array.from(facetFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);

      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const prev = facetsToRender[index - 1];
          if (elementToRender.className === prev.className) {
            const prevEl = document.getElementById(prev.id);
            if (prevEl) prevEl.after(elementToRender);
            return;
          }
        }
        if (elementToRender.parentElement) {
          const parent = document.querySelector(`#${elementToRender.parentElement.id} .js-filter`);
          if (parent) parent.before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && jsFilter) {
      FacetFiltersForm.renderCounts(countsToRender, jsFilter);
      FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(jsFilter.id));

      const newFacetDetailsElement = document.getElementById(jsFilter.id);
      const newElementSelector = newFacetDetailsElement?.classList.contains('mobile-facets__details')
        ? `.mobile-facets__close-button`
        : `.facets__summary`;
      const newElementToActivate = newFacetDetailsElement?.querySelector(newElementSelector);

      const isTextInput = event?.target?.getAttribute?.('type') === 'text';
      if (newElementToActivate && !isTextInput) newElementToActivate.focus();
    }
  }

  static renderActiveFacets(doc) {
    const selectors = ['.active-facets-mobile', '.active-facets-desktop'];
    selectors.forEach((selector) => {
      const src = doc.querySelector(selector);
      const dst = document.querySelector(selector);
      if (src && dst) dst.innerHTML = src.innerHTML;
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(doc) {
    const mobileSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];
    mobileSelectors.forEach((selector) => {
      const src = doc.querySelector(selector);
      const dst = document.querySelector(selector);
      if (src && dst) dst.innerHTML = src.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const drawer = mobileForm?.closest?.('menu-drawer');
    drawer?.bindEvents?.();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');
    if (sourceSummary && targetSummary) targetSummary.outerHTML = sourceSummary.outerHTML;

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');
    if (sourceHeaderElement && targetHeaderElement) targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');
    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }
      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    if (!target) return;
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');
    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  /* ---------- Form handling ---------- */
  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const target = event.target;

    if (target && target.classList && target.classList.contains('mobile-facets__checkbox')) {
      const form = target.closest('form');
      if (form) {
        const searchParams = this.createSearchParams(form);
        this.onSubmitForm(searchParams, event);
      }
      return;
    }

    const forms = [];
    const currentForm = target?.closest?.('form');
    const isMobile = currentForm?.id === 'FacetFiltersFormMobile';

    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
          forms.push(this.createSearchParams(form));
        }
      } else if (form.id === 'FacetFiltersFormMobile') {
        forms.push(this.createSearchParams(form));
      }
    });

    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const href = event.currentTarget?.href || '';
    const url = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/* ===== PriceRange ===== */
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((el) => {
      el.addEventListener('change', this.onRangeChange.bind(this));
      el.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') || '');
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));
    if (!Number.isNaN(min) && value < min) input.value = min;
    if (!Number.isNaN(max) && value > max) input.value = max;
  }
}
customElements.define('price-range', PriceRange);

/* ===== FacetRemove ===== */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if ((event.code || '').toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form?.onActiveFilterClick?.(event);
  }
}
customElements.define('facet-remove', FacetRemove);
